import type { ReactNode } from "react";

import { AiNotePopover } from "./AiNotePopover";
import type { AiNote } from "./utils/aiRelevance";
import { ValidationStatusIcon } from "./ValidationStatusIcon";

interface ValidationFieldItemProps {
  label: string;
  /** `undefined` means the API has no verdict for this field — no icon shown. */
  isValid: boolean | undefined;
  value: ReactNode;
  /**
   * Danish explanation shown when the field failed. Resolved by
   * `resolveCleFieldValidationReason` — never the API's raw English `reason`,
   * and never the evidence item's `aiRejectionReason`, which is a snapshot from
   * analysis time that contradicts the live per-field results after an edit.
   */
  reason?: string | null;
  /**
   * Ekstra kontekst under fejlbeskeden — fx hvad værdien blev sammenlignet med.
   * Vises kun på et fejlende felt: er feltet grønt, er der intet at forklare.
   */
  hint?: string | null;
  /**
   * AI'ens begrundelse bag et "AI"-mærke ved labelen. I modsætning til `hint`
   * vises mærket uanset dommen: begrundelsen er det, en sagsbehandler læser,
   * før de retter værdien — også på et grønt felt.
   */
  aiNote?: AiNote | null;
  className?: string;
}

export const ValidationFieldItem = ({
  label,
  isValid,
  value,
  reason,
  hint,
  aiNote,
  className,
}: ValidationFieldItemProps) => (
  <div className={className}>
    <h5 className="form-label d-flex align-items-center gap-2">
      {label}
      {aiNote ? <AiNotePopover fieldLabel={label} note={aiNote} /> : null}
    </h5>
    <div className="form-control--value d-flex align-items-start gap-2">
      <ValidationStatusIcon isValid={isValid} />
      <span>{value}</span>
    </div>
    {isValid === false && reason ? (
      <p className="text-danger small mb-0 mt-1">{reason}</p>
    ) : null}
    {isValid === false && hint ? (
      <p className="text-body-secondary small mb-0 mt-1">{hint}</p>
    ) : null}
  </div>
);
